import {
  Box,
  Button,
  Card,
  CardActionArea,
  Stack,
  Typography,
} from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { EmphasizedNumber, Icons, InformationModal } from 'utoniq-ui'

import {
  ItemResaleRaffleUser,
  Maybe,
  SalesStatus,
  StoreItemDetailFragment,
} from '@/clients/utoniq-core/schema'
import { useRouter, useTranslation } from '@/core'
import { encodeSkuId } from '@/core/sku'
import { dayjs, nextRaffleDateTime, secondsToHms } from '@/lib/dayjs'

type StoreButtonResaleBoxProps = {
  item: StoreItemDetailFragment
  userActiveResaleItemRaffle: Maybe<Pick<ItemResaleRaffleUser, 'id'>>
  skuId?: string
  restrected: boolean
  skuButtonActive?: boolean
  willResaleAvailable?: boolean
}

export const StoreButtonResaleBox: FC<StoreButtonResaleBoxProps> = ({
  item,
  skuId,
  userActiveResaleItemRaffle,
  restrected,
  skuButtonActive,
  willResaleAvailable,
}) => {
  const { t } = useTranslation('storeItemDetail')
  /** リセール応募重複モーダルの表示状態 */
  const [openResaleAppliedModal, setOpenResaleAppliedModal] =
    useState<boolean>(false)
  if (item.preSale) {
    return null
  }
  if (!skuButtonActive) {
    if (item.salesStatus === SalesStatus.Stopped) {
      return (
        <Typography
          color="text.secondary"
          variant="button"
          textAlign="center"
          sx={{ pb: 5 }}
        >
          {t('forStopSelling', '現在販売しておりません')}
        </Typography>
      )
    }
  }

  if (restrected)
    return (
      <Typography
        color="text.secondary"
        variant="button"
        textAlign="center"
        sx={{ pb: 5 }}
      >
        {t('forLimitedOnly', '対象の方のみ購入できる商品です。')}
      </Typography>
    )

  if (
    // リセール可能で、期間中の場合
    (item.isResaleAvailable ||
      // リセール期間前のリセールアイテム
      willResaleAvailable) &&
    !!item
  ) {
    return (
      <>
        <Stack spacing={6}>
          {/* リセール情報の表示 */}
          <ResaleCard
            item={item}
            skuId={skuId}
            willResaleAvailable={willResaleAvailable}
            userActiveResaleItemRaffle={userActiveResaleItemRaffle}
            openResaleAppliedModal={() => setOpenResaleAppliedModal(true)}
          />
        </Stack>
        {/* リセール応募重複注意モーダル */}
        <AppliedResaleModal
          open={openResaleAppliedModal}
          onClose={() => setOpenResaleAppliedModal(false)}
        />
      </>
    )
  }
  return null
}

type ResaleCardProps = {
  item: StoreItemDetailFragment
  skuId?: string
  userActiveResaleItemRaffle: Maybe<Pick<ItemResaleRaffleUser, 'id'>>
  willResaleAvailable?: boolean
  openResaleAppliedModal: () => void
}
const ResaleCard: FC<ResaleCardProps> = ({
  item,
  skuId,
  userActiveResaleItemRaffle,
  willResaleAvailable,
  openResaleAppliedModal,
}) => {
  const { t } = useTranslation('storeItemDetail')
  const { router, generatePath, routerPages, generateQueryPath } = useRouter()
  const [time, setTime] = useState<string>('')

  useEffect(() => {
    const interval = setInterval(() => {
      const nextRaffle = nextRaffleDateTime('YYYY/MM/DD HH:mm:ss')
      const diff = dayjs(nextRaffle, 'YYYY/MM/DD HH:mm:ss').diff(dayjs(), 's')
      if (diff < 0) {
        setTime('00:00:00')
        return
      }
      const { hours, minutes, seconds } = secondsToHms(diff)
      setTime(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`)
    }, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <Card
      sx={{ cursor: item.isResaleAvailable ? 'pointer' : 'initial' }}
      elevation={0}
    >
      <CardActionArea
        onClick={() => {
          if (!item.isResaleAvailable) return

          // すでにリセール応募/購入済みの場合、再応募不可
          if (userActiveResaleItemRaffle) {
            openResaleAppliedModal()
            return
          }
          router.push(
            generateQueryPath(
              routerPages.ResaleApplicationDetail,
              { itemId: item.id },
              { skuId: encodeSkuId(skuId ?? '') }
            )
          )
        }}
        disableTouchRipple={!!willResaleAvailable}
        sx={{ p: 5, '&:last-child': { pb: 5 } }}
      >
        <Stack>
          <Box
            sx={{
              borderRadius: 1,
              bgcolor: 'primary.dark',
              color: 'white',
              typography: 'button',
              textAlign: 'center',
              py: '6px',
            }}
          >
            {item.isResaleAvailable
              ? t('underResaleExhibition', 'リセール出品中')
              : t('beforeResaleExhibition', 'リセール出品待ち')}
          </Box>
          {willResaleAvailable ? (
            <Stack justifyContent="center" sx={{ pt: 4 }}>
              <Typography variant="subtitle2" align="center">
                {t('resaleApplicationPeriod', 'リセール応募期間')}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {item.metadata?.ticket?.resalePeriod?.formattedStartedAt} 〜{' '}
                {item.metadata?.ticket?.resalePeriod?.formattedEndedAt}
              </Typography>
            </Stack>
          ) : (
            <>
              <Stack
                sx={{ pt: 4 }}
                direction="row"
                justifyContent="space-between"
              >
                <Stack>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('exhibitNumber', '出品枚数')}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Icons.Ticket fontSize="medium" />
                    <EmphasizedNumber variant="h2">
                      {t('numberValue', '枚', {
                        number: item.resaleOpenCount ?? 0,
                      })}
                    </EmphasizedNumber>
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      {t('applicationNumber', '応募枚数')}
                    </Typography>
                    <Typography variant="overline">
                      {t('numberValue', {
                        number: item.resaleRaffleAppliedCount ?? 0,
                      })}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    color="error.main"
                    align="right"
                  >
                    {t('nextLottery', { date: time })}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    align="right"
                  >
                    {t('resaleApplicationPeriod', 'リセール応募期間')}
                  </Typography>
                  <Typography variant="subtitle1" align="right">
                    {t('to', {
                      date: item.metadata?.ticket?.resalePeriod
                        ?.formattedEndedAt,
                    })}
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={2}
              >
                <Typography
                  color="primary.main"
                  variant="caption"
                  align="right"
                >
                  {t('checkResaleTicket', '詳細を確認する')}
                </Typography>
                <Icons.RightArrow
                  sx={{ width: 8, height: 8 }}
                  color="primary"
                />
              </Stack>
            </>
          )}
        </Stack>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 10,
            maxWidth: '600px',
            margin: '0 auto',
            bgcolor: 'grey.50',
            pb: 'env(safe-area-inset-bottom, 0)',
          }}
        >
          <Stack alignItems="center" sx={{ py: 3, px: 5, bgcolor: 'grey.50' }}>
            <Button
              variant={'contained'}
              size="large"
              disabled={!!willResaleAvailable}
              sx={{ width: '100%' }}
            >
              {t('beforeResaleExhibition')}
            </Button>
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  )
}

function pad(d: number): string {
  return d < 10 ? `0${d}` : d.toString()
}

/**
 * すでにアイテムの転売応募をしている状態のとき、その旨を表示するモーダル
 */
const AppliedResaleModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  const { t } = useTranslation('storeItemDetail')
  const { routerPages, generatePath } = useRouter()

  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={t('resaleAlreadyApplied', 'すでにリセールに応募しています。')}
      content={t(
        'resaleAlreadyAppliedDesc',
        'このアイテムのリセール応募をすでに行なっているため、重ねて応募することはできません。'
      )}
      cancelText={t('close', '閉じる')}
      buttons={
        <Button
          size="large"
          sx={{ width: 212 }}
          href={generatePath(routerPages.ResaleManagement, { tab: 'raffle' })}
        >
          {t('toResaleManagement', 'リセール管理画面へ')}
        </Button>
      }
    />
  )
}
